import { AppNS } from 'app';
import { FileInfo } from 'app/ui/components/multi-file-uploader/MultiFileUploader';
import axios, { Canceler } from 'axios';

import { API_URL } from '../../core/config';
import { ApiNS } from '../api';
import { parseInvoiceData } from './invoices-parsers';

const URL = 'invoices';
const APPROVE_AND_PAY = 'approve-and-pay';
const DOCUMENTS_URL = 'documents';

const CancelToken = axios.CancelToken;
let cancel: Canceler;

export function apiGetInvoiceById(
  invoiceId: string
): Promise<AppNS.ProjectInvoice> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${URL}/${invoiceId}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(parseInvoiceData(data.model));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiPayInvoice(reqData: AppNS.Params): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .patch(`${API_URL}/${URL}/${APPROVE_AND_PAY}.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          if (data.errors) {
            reject(data.errors);
          }
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiDeleteInvoice(id: string): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${API_URL}/${URL}`, { params: { id } })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiCreateInvoice(reqData: AppNS.Params): Promise<string> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${URL}.json`, reqData)
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetInvoices(
  query: string,
  dateFrom: string,
  dateTo: string,
  page: number,
  searchBy: string,
  state: string,
  perPage: number = 20
): Promise<ApiNS.InvoicesListResponse> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${URL}.json`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: {
          query,
          search_by: searchBy,
          date_from: dateFrom,
          date_to: dateTo,
          page,
          state,
          per_page: perPage,
        },
      })
      .then(({ data }) => {
        if (data.results) {
          resolve(data);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiGetInvoicesCsv(
  query: string,
  dateFrom: string,
  dateTo: string,
  searchBy: string,
  state: string
): Promise<Blob> {
  if (cancel !== undefined) {
    cancel();
  }
  return new Promise((resolve, reject) => {
    axios
      .get(`${API_URL}/${URL}.csv`, {
        cancelToken: new CancelToken(function executor(c) {
          // An executor function receives a cancel function as a parameter
          cancel = c;
        }),
        params: {
          query,
          search_by: searchBy,
          date_from: dateFrom,
          date_to: dateTo,
          state,
        },
        headers: {
          Accept: 'text/csv',
        },
      })
      .then(({ data }) => {
        if (data) {
          resolve(new Blob([data], { type: 'text/csv' }));
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiAddInvoiceDocument(
  reqData: ApiNS.AddStaffDocumentRequest
): Promise<FileInfo> {
  return new Promise((resolve, reject) => {
    axios
      .post(`${API_URL}/${DOCUMENTS_URL}.json?return_properties=true`, reqData)
      .then(({ data }) => {
        if (data.model) {
          // Added timeout because of s3 404 error when private url is invoked immediately
          setTimeout(() => {
            resolve({
              id: data.model.id,
              documentId: data.model.id,
              thumbnail: '',
              url: data.model.file.url,
              uppyId: '',
              name: data.model.properties.file.fileName,
              isLoaded: true,
              isRemoved: false,
            });
          }, 500);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}

export function apiDeleteInvoiceDocument(id: string): Promise<FileInfo> {
  return new Promise((resolve, reject) => {
    axios
      .delete(`${API_URL}/${DOCUMENTS_URL}.json`, {
        params: { id },
      })
      .then(({ data }) => {
        if (data.model) {
          resolve(data.model.id);
        } else {
          reject('Invalid response data');
        }
      })
      .catch(reject);
  });
}
